import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React from "react";
import ExcelSummaryBox from "../../sections/ExcelSummaryBox";
import { useSelector } from "react-redux";
import { selectEndorsmentFormData } from "src/features/rfqDataSlice";
import FireFloaterChangesTable from "./FireFloaterChangesTable";

const formatVal = (v: any) => {
  if (Array.isArray(v)) return v.map((x) => (x?.value ?? x)).join(", ");

  if (v == null || v === "") return "N/A";

  // ✅ If string looks like ISO datetime, format to dd-mm-yyyy
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }

  return v;
};


const SumInsuredChanges = ({ sumInsuredChanges, excelChanges }) => {
  const endorsementFormData = useSelector(selectEndorsmentFormData);
  if (!sumInsuredChanges) return null;

  return (
    <Box sx={{ mt: 3 }}>
      {Object.entries(sumInsuredChanges).map(([sectionId, section]: any) => {
        const fieldLabelMap = section.fieldLabelMap || {};
        const sectionType = section.labelValuePairItem ? "labelValuePair" : "group";
        const groups = section.groups || {};

        const excelSectionInfo = excelChanges?.[sectionId];


        const hasGroupData = Object.values(section?.groups || {}).some((group: any) => {
          const groupId = group?.id;

          const hasManualChanges =
            group?.changes?.updates?.length > 0 ||
            group?.changes?.new?.length > 0 ||
            group?.changes?.deleted?.length > 0;
           const hasExcelGroupData = Object.keys(excelSectionInfo?.groups || {}).length > 0;

          return hasManualChanges || hasExcelGroupData;
        });
        const hasFireFloaterChanges = section?.fireFloaterChanges?.length > 0;

        if (
          sectionType !== "labelValuePair" &&
          !hasGroupData &&
          !hasFireFloaterChanges
        ) {
          return;
        }

        return (
          <Box key={sectionId} sx={{ mb: 3, overflow: 'scroll' }}>
            <Box sx={{ display: 'block', minWidth: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Sum Insured Section: {section.sectionName || "N/A"}

              </Typography>


              {sectionType === "labelValuePair" ? (
                <>
                  {section.labelValuePairItem?.updates?.length > 0 && (
                    <>
                      <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                        Updated Entries
                      </Typography>
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell>Description</TableCell>
                            <TableCell>Old Value</TableCell>
                            <TableCell>New Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.labelValuePairItem.updates.map((item: any, i: number) => {
                            const oldVal = item.changes?.sumInsured?.old;
                            const newVal = item.changes?.sumInsured?.new;
                            const isChanged = oldVal !== newVal;

                            return (
                              <TableRow key={i}>
                                <TableCell>{item.description}</TableCell>

                                <TableCell  >
                                  {isChanged ? (
                                    <span style={{ color: "red", textDecoration: "line-through" }}>
                                      {oldVal}
                                    </span>
                                  ) : (
                                    oldVal
                                  )}
                                </TableCell>

                                <TableCell >
                                  {isChanged ? (
                                    <span style={{ color: "green", fontWeight: 600 }}>{newVal}</span>
                                  ) : (
                                    newVal
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>

                      </Table>
                    </>
                  )}

                  {/* New Entries */}
                  {section.labelValuePairItem?.new?.length > 0 && (
                    <>
                      <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                        Newly Added Entries
                      </Typography>
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell>Description</TableCell>
                            <TableCell>Sum Insured</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.labelValuePairItem.new.map((item: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.sumInsured}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}

                  {/* Deleted Entries */}
                  {section.labelValuePairItem?.deleted?.length > 0 && (
                    <>
                      <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                        Deleted Entries
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell>Description</TableCell>
                            <TableCell>Sum Insured</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.labelValuePairItem.deleted.map((item: any, i: number) => (
                            <TableRow key={i}>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.sumInsured}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </>
              ) : (
                <>
                  <FireFloaterChangesTable fireFloaterChanges={section?.fireFloaterChanges} />

                  {

                    Object.entries(groups).map(([groupId, group]: any) => {
                      const { updates = [], new: newEntries = [], deleted = [] } = group.changes || {};
                      const hasArrayChanges = updates.length || newEntries.length || deleted.length;

                      // ✅ Check excel summary existence
                      const groupData =
                        endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId] || {};
                      const hasExcelSummary =
                        groupData?.excelPathForInsert ||
                        groupData?.excelPathForDelete ||
                        groupData?.excelInsertSummary ||
                        groupData?.excelDeleteSummary;

                      if (!hasArrayChanges && !hasExcelSummary) return null;

                      const sampleRow = updates[0] || newEntries[0] || deleted[0] || {};
                      const fieldKeys = Object.keys(sampleRow).filter(
                        (k) => !["changes", "id", "_originalId"].includes(k)
                      );

                      return (
                        <Box key={groupId} sx={{ mb: 3 }}>



                          {updates.length > 0 && (
                            <>
                              <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                                Updated Entries
                              </Typography>
                              <Table size="small" sx={{ mb: 2 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ background: "#f5f5f5", width: 160 }} />
                                    {fieldKeys.map((key) => (
                                      <TableCell key={key} sx={{ background: "#f5f5f5" }}>
                                        {fieldLabelMap[key] || key}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {updates.map((update: any, i: number) => {
                                    const changes = update.changes || {};
                                    return (
                                      <TableRow key={i}>
                                        <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                                        {fieldKeys.map((key) => {
                                          const oldVal = formatVal(changes[key]?.old ?? update[key]);
                                          const newVal = formatVal(changes[key]?.new ?? update[key]);
                                          const isChanged = changes[key] !== undefined;

                                          return (
                                            <TableCell key={key}>
                                              {isChanged ? (
                                                <div>
                                                  <div style={{ color: "red", textDecoration: "line-through", whiteSpace: 'nowrap' }}>
                                                    {oldVal}
                                                  </div>
                                                  <div style={{ color: "green", fontWeight: 600, whiteSpace: 'nowrap' }}>{newVal}</div>
                                                </div>
                                              ) : (
                                                oldVal
                                              )}
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>

                              </Table>
                            </>
                          )}

                          {/* ✅ New Entries */}
                          {newEntries.length > 0 && (
                            <>
                              <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                                Newly Added Entries
                              </Typography>
                              <Table size="small" sx={{ mb: 2 }}>
                                <TableHead>
                                  <TableRow sx={{ background: "#f5f5f5" }}>
                                    {fieldKeys.map((key) => (
                                      <TableCell key={key}>{fieldLabelMap[key] || key}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {newEntries.map((entry: any, i: number) => (
                                    <TableRow key={i}>
                                      {fieldKeys.map((key) => (
                                        <TableCell key={key}>{formatVal(entry[key])}</TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </>
                          )}

                          {/* ❌ Deleted Entries */}
                          {deleted.length > 0 && (
                            <>
                              <Typography sx={{ color: "#fff", background: "#5569ff", px: 1, py: 0.5 }}>
                                Deleted Entries
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ background: "#f5f5f5" }}>
                                    {fieldKeys.map((key) => (
                                      <TableCell key={key}>{fieldLabelMap[key] || key}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {deleted.map((entry: any, i: number) => (
                                    <TableRow key={i}>
                                      {fieldKeys.map((key) => (
                                        <TableCell key={key}>{formatVal(entry[key])}</TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </>
                          )}

                          {/* ✅ Always render ExcelSummaryBox if excel summary exists */}
                          {hasExcelSummary && (
                            <ExcelSummaryBox
                              endorsementFormData={endorsementFormData}
                              sectionId={sectionId}
                              groupId={groupId}
                            />
                          )}
                        </Box>
                      );
                    })
                  }
                </>

              )}

            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SumInsuredChanges;
